import { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import {
  InlineMessage,
  Text,
  ThemeTypographyKey,
} from "chameleon/lib/components";
import { PaymentMethodTitle } from "chameleon/lib/components/payment-method-title/payment-method-title";

import { FormSection } from "checkout-experience/components/forms/form-section";
import { PayByBankAccountType } from "checkout-experience/components/payment-method/pay-options/pay-by-bank/types";
import { useLeadingSlotForPaymentSummary } from "checkout-experience/components/payment-method/payment-method-summary-display-helpers/use-leading-slot-for-payment-summary";
import { useTitleForPaymentSummary } from "checkout-experience/components/payment-method/payment-method-summary-display-helpers/use-title-for-payment-summary";
import {
  titleWrapperStyle,
  wrapperStyle,
} from "checkout-experience/components/payment-method/payment-method-summary-display.css";
import { SurchargeExtended } from "checkout-experience/hooks/use-surcharge";
import { PaymentType } from "checkout-query-client/types/form-types";

import { CardNetwork, EcpBankAccountType } from "frontend-client";

export interface PaymentMethodDisplay {
  last4?: string;
  ddaAccountNumberLast4?: string;
  ibanLast4?: string;
  network?: string | CardNetwork;
  bankAccountType?: EcpBankAccountType;
  expirationMonth?: number;
  expirationYear?: number;
  paymentType: PaymentType;
  bankName?: string;
  last4digitsOfAccountNumber?: string;
  accountType?: PayByBankAccountType;
  formId?: string;
  surcharge?: SurchargeExtended | null;
}
interface PaymentMethodDefaultProps {
  paymentMethodSummary: PaymentMethodDisplay;
  editSavedPaymentMethod?: boolean;
  isExpired: boolean;
  showForm?: boolean;
  isCollapsed?: boolean;
  trailingSlot?: ReactElement;
  titleStyleVariant?: ThemeTypographyKey;
  surchargeText?: string;
}

const PaymentMethodSummaryDisplay = ({
  isExpired,
  showForm,
  editSavedPaymentMethod,
  paymentMethodSummary,
  titleStyleVariant,
  trailingSlot,
  isCollapsed = true,
  surchargeText,
}: PaymentMethodDefaultProps) => {
  const { paymentType } = paymentMethodSummary || {};
  if (!paymentType) return <></>;

  const { t } = useTranslation();
  const title = useTitleForPaymentSummary(paymentMethodSummary);
  const leadingSlot = useLeadingSlotForPaymentSummary(paymentMethodSummary);

  return (
    <div className={wrapperStyle}>
      <div className={titleWrapperStyle}>
        <PaymentMethodTitle
          leadingSlot={leadingSlot}
          title={title}
          trailingSlot={trailingSlot}
          titleStyleVariant={titleStyleVariant}
        />
        {surchargeText ? (
          <Text styleVariant="body" tagElement="span">
            {surchargeText}
          </Text>
        ) : null}
      </div>
      {isExpired && isCollapsed && paymentType === PaymentType.creditCard ? (
        <InlineMessage
          id={"card-expired-message"}
          message={t("paymentMethod.errors.cardExpired")}
          type="error"
        />
      ) : null}
      {showForm ? (
        <FormSection
          showActionSection={false}
          editSavedPaymentMethod={editSavedPaymentMethod}
        />
      ) : null}
    </div>
  );
};

export default PaymentMethodSummaryDisplay;
